import { useEffect, useId, useRef, useState } from 'react'
import { Image, Platform, Pressable, Text, useWindowDimensions, View } from 'react-native'
import * as Linking from 'expo-linking'
import { Stack, useLocalSearchParams } from 'expo-router'
import NativeQRCode from 'react-native-qrcode-svg'
import { useTranslation } from 'react-i18next'
import { ClipPath, Defs, G, LinearGradient, Path, Rect, Stop, Svg } from 'react-native-svg'

import { BASE_URL } from '@/constants/config'
import { marketPresentation } from '@/config/marketPresentation'
import { formatRemainingTime } from '@/utils/formatter'
import { useAutomaticCountdown } from '@/hooks/useCountdown'
import { Button } from '@/components/base'
import NavigationBar from '@/components/NavigationBar'

type QRCodeModule = typeof import('qrcode')

function BigLogo() {
  const id = useId()
  return (
    <Svg width={'192'} height={'114'} viewBox={'0 0 64 38'} fill={'none'}>
      <G clipPath={`url(#${id}_clip0_7576_47160)`}>
        <Path
          fillRule={'evenodd'}
          clipRule={'evenodd'}
          d={
            'M47.8153 22.2951L46.2866 22.3699C46.7381 22.155 46.9622 21.797 46.9622 21.2958C46.9622 20.944 46.8532 20.6607 46.6384 20.4427C46.4236 20.2279 46.1372 20.1189 45.7854 20.1189C45.4336 20.1189 45.1627 20.2279 44.9479 20.4427C44.7238 20.6669 44.6086 20.9533 44.6086 21.2958C44.6086 21.5697 44.6864 21.8001 44.8452 21.9869C45.0008 22.2017 45.1845 22.3449 45.39 22.4134L43.0364 22.5162L43.1547 19.9757H46.3458C46.9249 19.9757 47.2144 19.6831 47.2144 19.0947C47.2144 18.5063 46.9249 18.2385 46.3458 18.2852L43.2138 18.4751L43.2885 16.8282H46.9062C47.3265 16.8282 47.5382 16.6134 47.5382 16.1806V15.5486L48.0394 15.4895C48.3725 15.449 48.5531 15.2653 48.5842 14.9322L48.6714 13.8301C48.7399 12.7716 48.2449 12.2641 47.1864 12.3015L43.4816 12.4914C43.5407 12.3544 43.5687 12.2081 43.5687 12.0493C43.5687 11.7473 43.4567 11.4827 43.2294 11.2554C43.0146 11.0406 42.7437 10.9316 42.4199 10.9316C42.1148 10.9316 41.8564 11.0406 41.6416 11.2554C41.4175 11.4796 41.3023 11.7442 41.3023 12.0493C41.3023 12.2454 41.3458 12.426 41.433 12.5941L37.9928 12.756V12.4914C37.9928 12.3139 37.9057 12.2268 37.7282 12.2268H37.0371C36.4113 12.2268 36.1155 12.5443 36.156 13.1825L36.2743 14.7983C36.3148 15.3463 36.6074 15.586 37.1554 15.5175L37.2737 15.5019C37.7344 15.4615 37.9648 15.2093 37.9648 14.7392V14.048H46.8875V15.1066L37.9337 15.6202C37.5321 15.6389 37.3795 15.8444 37.4791 16.2367C37.5757 16.629 37.8278 16.8251 38.2294 16.8251H41.4206V18.5748L38.273 18.7647C37.8621 18.7927 37.7095 19.0013 37.8185 19.3811C37.915 19.7734 38.1672 19.9695 38.5688 19.9695H41.4206V22.5847L36.76 22.8057C36.349 22.8151 36.1965 23.0205 36.3054 23.4221C36.4144 23.8051 36.6635 23.995 37.0557 23.995H47.8184C48.3974 23.995 48.687 23.7023 48.687 23.1139C48.687 22.5255 48.3974 22.2609 47.8184 22.292L47.8153 22.2951ZM45.8041 22.0087C45.7729 21.8281 45.7013 21.6912 45.5301 21.4981C45.3526 21.302 45.0351 21.2522 45.0351 21.2522C45.0351 21.2522 45.3091 21.2273 45.5426 21.0093C45.7761 20.7914 45.7978 20.4739 45.7978 20.4739C45.7978 20.4739 45.8196 20.7945 46.0531 21.0093C46.2866 21.2273 46.5606 21.2522 46.5606 21.2522C46.5606 21.2522 46.243 21.2895 46.0749 21.4888C45.9068 21.6849 45.8352 21.8281 45.8041 22.0087ZM60.533 15.6669L58.0331 15.8133L58.0611 13.5935C58.6868 13.5437 59.3064 13.4939 59.9135 13.4378C60.5206 13.3849 61.1245 13.3226 61.7223 13.2542C62.4166 13.167 62.6127 12.8027 62.3107 12.1676C61.9869 11.5418 61.4763 11.3021 60.7821 11.4484C59.3033 11.7535 57.8307 12.0151 56.3643 12.233C54.898 12.454 53.4223 12.6408 51.931 12.7996C51.5792 12.8401 51.4453 13.03 51.5356 13.3725C51.6135 13.7243 51.8283 13.8924 52.1832 13.8706C52.8588 13.8519 53.525 13.8301 54.1819 13.8114C54.8388 13.7927 55.4895 13.7616 56.1371 13.7243V15.9285L53.6962 16.0748V15.8102C53.6962 15.6327 53.6091 15.5455 53.4316 15.5455H52.7405C52.0929 15.5455 51.7847 15.8631 51.8158 16.5013L52.0057 21.2771C52.0244 21.8468 52.314 22.0741 52.8743 21.9682C53.4223 21.8593 53.6962 21.5324 53.6962 20.9844V17.4291H56.1371V23.5996C56.1371 24.1569 56.411 24.3873 56.959 24.2907C57.5162 24.1942 57.8027 23.8642 57.812 23.3069L57.9739 17.4291H60.6358V20.0722L59.2971 20.2933C58.9826 20.3431 58.8269 20.5237 58.8269 20.8381C58.8269 21.1526 58.9826 21.3518 59.2971 21.411L60.561 21.6756C61.7285 21.9122 62.3294 21.439 62.3699 20.2653L62.4726 17.5754C62.5318 16.2336 61.8842 15.5985 60.533 15.6669Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M37.7007 27.273C37.707 27.2045 37.7505 27.1703 37.8284 27.1703C37.9062 27.1858 37.9436 27.2263 37.9436 27.2979C37.928 27.466 37.8969 27.6497 37.8502 27.849V29.7792C37.8439 29.8415 37.8035 29.8757 37.735 29.8819C37.6571 29.8757 37.6198 29.8415 37.6198 29.7792V28.4343C37.5824 28.5401 37.542 28.6148 37.5046 28.6522C37.4579 28.7145 37.405 28.7207 37.3427 28.674C37.296 28.6211 37.2929 28.5588 37.3303 28.4903C37.5451 28.1447 37.6665 27.74 37.6976 27.273H37.7007ZM38.4728 27.217C38.4884 27.1485 38.5351 27.1205 38.6098 27.136C38.6721 27.1578 38.7032 27.2045 38.7032 27.273C38.6876 27.3197 38.6721 27.3664 38.6565 27.41C38.6409 27.4536 38.6347 27.4909 38.6347 27.5127H39.9112C40.0637 27.5283 40.1478 27.6092 40.1633 27.7556C40.1415 27.9393 40.1011 28.0887 40.0481 28.2039C40.0108 28.2724 39.9516 28.2879 39.8769 28.2506C39.8147 28.2132 39.7991 28.1541 39.8302 28.0793C39.8614 28.0171 39.8831 27.933 39.8987 27.8272C39.9143 27.7587 39.8925 27.7244 39.8302 27.7244H39.2885V29.5986C39.3259 29.8664 39.1048 29.9349 38.6223 29.8072C38.5444 29.7854 38.5133 29.7294 38.5289 29.6453C38.56 29.5831 38.6129 29.5582 38.6908 29.5644C38.9585 29.6547 39.0768 29.6516 39.0488 29.5519V27.7244H38.5662C38.4573 27.9766 38.3296 28.2039 38.174 28.4031C38.1117 28.4654 38.0525 28.4747 37.9903 28.4374C37.9436 28.3845 37.9467 28.3222 38.0027 28.2537C38.2113 27.961 38.367 27.6186 38.4728 27.2201V27.217ZM38.5413 28.1945C38.5475 28.126 38.5942 28.0949 38.6783 28.1011C38.7468 28.1229 38.781 28.1727 38.781 28.2506C38.7188 28.6958 38.5942 29.1254 38.4012 29.5364C38.3545 29.5986 38.2954 29.6142 38.2175 29.5831C38.149 29.5519 38.1335 29.4959 38.1708 29.4118C38.3701 28.9666 38.4946 28.5619 38.5382 28.1945H38.5413ZM39.8738 29.5146C39.7835 29.0787 39.6559 28.6646 39.494 28.2724C39.4629 28.2039 39.4815 28.151 39.55 28.1105C39.6185 28.0887 39.6714 28.1074 39.7119 28.1665C39.8582 28.481 39.989 28.9075 40.1042 29.443C40.1198 29.5115 40.0886 29.5613 40.0108 29.5924C39.933 29.608 39.8894 29.58 39.8738 29.5115V29.5146Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M42.3815 27.1952C42.4749 27.1111 42.5434 27.1267 42.5901 27.2419C42.5839 27.2792 42.5683 27.3384 42.5434 27.4131C42.5372 27.4598 42.5278 27.5034 42.5216 27.5501H42.8205C42.9512 27.5438 43.0135 27.6154 43.0042 27.768V29.527C43.0042 29.6484 42.9419 29.7107 42.8205 29.7107H42.0391C41.9239 29.7107 41.8709 29.6546 41.8772 29.5395V27.7462C41.8616 27.6092 41.9301 27.5438 42.0857 27.5501H42.2943C42.3161 27.4816 42.3442 27.3726 42.3753 27.2294C42.3597 27.329 42.3628 27.3166 42.3877 27.1952H42.3815ZM42.1044 28.7456V29.4336C42.0982 29.4959 42.1231 29.5239 42.1854 29.5239H42.6804C42.7333 29.5332 42.7644 29.5052 42.7738 29.4429V28.7425H42.1075L42.1044 28.7456ZM42.2321 27.7555C42.1387 27.7555 42.0982 27.7836 42.1044 27.8365V28.537H42.7707V27.8489C42.7707 27.7867 42.7427 27.7555 42.6897 27.7555H42.229H42.2321ZM43.3466 27.1952C43.3466 27.1329 43.384 27.108 43.4618 27.1142C43.5241 27.1204 43.5583 27.1578 43.5646 27.2169C43.5646 27.2792 43.5521 27.3633 43.5303 27.4691H44.2308C44.4145 27.4629 44.5017 27.547 44.4954 27.7213V28.6397C44.4954 29.3433 44.4176 29.72 44.2651 29.7667C44.0502 29.8726 43.7669 29.8601 43.4151 29.72C43.3466 29.6889 43.3186 29.636 43.3342 29.5581C43.356 29.5052 43.4058 29.4896 43.4836 29.5114C43.7607 29.6111 43.9631 29.6391 44.0938 29.5924C44.2246 29.5613 44.29 29.2561 44.29 28.674V27.7898C44.29 27.7213 44.2433 27.6871 44.153 27.6871H43.4743C43.4214 27.8645 43.356 28.0046 43.2781 28.1136C43.2314 28.1665 43.1785 28.179 43.1163 28.1478C43.0633 28.1011 43.0509 28.0482 43.082 27.9859C43.2439 27.7493 43.3311 27.4847 43.3466 27.192V27.1952ZM43.658 28.1354C43.8043 28.3813 43.9382 28.6615 44.0596 28.976C44.0814 29.0444 44.0596 29.0974 43.9911 29.1378C43.9226 29.169 43.8759 29.1503 43.8541 29.0818C43.7451 28.8047 43.6206 28.5308 43.4743 28.2537C43.4431 28.1914 43.4556 28.1416 43.5085 28.1042C43.5614 28.0731 43.6113 28.0856 43.658 28.1385V28.1354Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M48.7005 28.0201C48.7783 28.0263 48.8188 28.0668 48.8281 28.1353C48.8219 28.2131 48.7815 28.2505 48.713 28.2505H47.7572V28.677H48.6787C48.7565 28.6832 48.797 28.7237 48.8064 28.7922C48.797 28.87 48.7565 28.9074 48.6787 28.9074H47.7572V29.5861H48.906C48.9745 29.5861 49.0118 29.6203 49.0212 29.6888C49.0212 29.7667 48.9869 29.8071 48.9184 29.8165H46.4807C46.3967 29.8071 46.35 29.7698 46.3438 29.7013C46.35 29.6235 46.3936 29.5861 46.4714 29.5861H47.5175V28.9074H46.6893C46.6053 28.9074 46.5586 28.87 46.5523 28.7922C46.5586 28.7237 46.6053 28.6864 46.6893 28.677H47.5175V28.2505H46.7018C46.6177 28.2505 46.5679 28.2163 46.5523 28.1478C46.5523 28.0699 46.5928 28.0295 46.68 28.0201H48.7036H48.7005ZM47.6544 27.1359C47.7074 27.2511 47.7541 27.3352 47.7914 27.3881H48.8717C49.0181 27.3881 49.0834 27.4535 49.0679 27.5843C49.0367 27.7057 49.0025 27.8115 48.9651 27.8956C48.9184 27.9796 48.8624 28.0108 48.7939 27.9859C48.7161 27.9547 48.6974 27.8987 48.7379 27.8146C48.8281 27.6621 48.825 27.5936 48.7254 27.6092H46.6551C46.6022 27.6092 46.5772 27.6372 46.5866 27.6901V27.8178C46.5804 27.8862 46.5399 27.9205 46.4714 27.9205C46.3936 27.9205 46.3562 27.8862 46.3562 27.8178V27.5749C46.3406 27.4442 46.406 27.3819 46.5523 27.3912H47.5299C47.5299 27.3912 47.5019 27.3414 47.4739 27.2885C47.4583 27.2667 47.4521 27.248 47.4521 27.2325C47.4209 27.1795 47.4365 27.1328 47.4988 27.0955C47.561 27.0737 47.614 27.0892 47.6607 27.1422L47.6544 27.1359ZM48.3331 29.5145C48.2491 29.4149 48.1401 29.2997 48.0094 29.1689C47.9471 29.116 47.9377 29.0631 47.9751 29.007C48.028 28.9603 48.0903 28.9603 48.1588 29.007C48.2802 29.1129 48.4016 29.2405 48.5168 29.3868C48.5635 29.4647 48.5573 29.5176 48.5044 29.5487C48.4421 29.5799 48.3861 29.5674 48.3331 29.5145Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M52.2664 27.7808C52.2726 28.4999 52.201 28.97 52.0484 29.1817C51.9332 29.4028 51.5877 29.5958 51.0117 29.7577C50.9121 29.7795 50.8467 29.7577 50.8156 29.6892C50.8 29.6114 50.8436 29.5616 50.9432 29.5398C51.4414 29.4028 51.7402 29.2378 51.8398 29.0448C51.9706 28.8673 52.0329 28.4501 52.0235 27.7932C52.0235 27.7154 52.0609 27.6749 52.1387 27.6656C52.2166 27.6718 52.257 27.7123 52.2664 27.7808ZM53.2875 28.9856C53.2813 29.079 53.2377 29.1226 53.1599 29.1226C53.0821 29.1226 53.0447 29.0759 53.0447 28.9856V27.6282C53.0509 27.5597 53.0229 27.5255 52.9513 27.5255H51.376C51.2919 27.5255 51.2546 27.5597 51.2608 27.6282V29.0074C51.2546 29.0852 51.211 29.1226 51.1331 29.1226C51.0553 29.1164 51.0179 29.0728 51.0179 28.9949V27.4788C51.0024 27.3418 51.0678 27.2764 51.2141 27.2826H53.1007C53.2222 27.2764 53.2844 27.348 53.2844 27.5006V28.9825L53.2875 28.9856ZM52.5528 29.0323C52.8984 29.2004 53.2035 29.3934 53.4743 29.6083C53.5273 29.6612 53.5366 29.7235 53.4961 29.7919C53.4494 29.8449 53.3965 29.848 53.3342 29.8044C53.0571 29.5834 52.7676 29.3934 52.4594 29.2409C52.3816 29.2098 52.3598 29.1599 52.3909 29.0915C52.422 29.0292 52.475 29.0105 52.5528 29.0354V29.0323Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M55.5478 27.2173C55.5634 27.1395 55.6101 27.1052 55.6848 27.1146C55.7471 27.1301 55.7782 27.1799 55.7782 27.264C55.7626 27.3169 55.7502 27.3512 55.744 27.3667H56.1705C56.2483 27.373 56.2888 27.4134 56.2981 27.4819C56.2919 27.5504 56.2483 27.5847 56.1705 27.5847H55.6879C55.6506 27.7061 55.607 27.8057 55.5603 27.8835H56.102C56.1705 27.8898 56.2078 27.9302 56.2172 27.9987C56.211 28.0766 56.1705 28.1139 56.102 28.1139H55.8249V28.4938H56.1238C56.1923 28.5 56.2265 28.5405 56.2265 28.6089C56.2265 28.6774 56.1923 28.7117 56.1238 28.7117H55.8249V29.5149C55.8187 29.5367 55.8187 29.5492 55.8249 29.5492C55.8311 29.5492 55.8467 29.546 55.8716 29.5367C55.9557 29.4526 56.021 29.381 56.0677 29.3188C56.1144 29.2721 56.1705 29.269 56.239 29.3063C56.2919 29.3592 56.3012 29.4122 56.2608 29.4682C56.0989 29.6363 55.965 29.7546 55.8592 29.8231C55.7751 29.8698 55.7097 29.8761 55.663 29.8449C55.6163 29.8076 55.5945 29.7391 55.5945 29.6363V28.7055H55.3766C55.2988 28.7055 55.2583 28.6712 55.2489 28.6027C55.2552 28.5342 55.2988 28.4969 55.3766 28.4875H55.5945V28.1077H55.4575C55.389 28.1918 55.3237 28.2198 55.2614 28.1886C55.2085 28.1419 55.2054 28.0735 55.2489 27.9801C55.3704 27.7746 55.47 27.5162 55.5478 27.2111V27.2173ZM56.6624 27.2298C56.653 27.1519 56.6904 27.1083 56.7651 27.0928C56.8336 27.1021 56.8772 27.1426 56.8928 27.2204C56.899 27.3979 56.9146 27.6065 56.9395 27.8524H56.9955C57.2788 27.8057 57.5372 27.759 57.7645 27.7154C57.833 27.7154 57.8766 27.7466 57.8921 27.8057C57.8984 27.8742 57.8703 27.9178 57.7987 27.9334C57.4469 28.0019 57.1667 28.0454 56.9581 28.061C56.9581 28.0921 56.9706 28.1824 56.9924 28.3381C56.9924 28.3754 56.9955 28.4004 57.0048 28.4066C57.1356 28.3848 57.257 28.3599 57.3722 28.3381C57.5715 28.2914 57.7147 28.2603 57.7987 28.2478C57.8672 28.2478 57.9139 28.2789 57.9357 28.3381C57.9419 28.4159 57.9139 28.4595 57.8423 28.4751C57.5901 28.528 57.3255 28.5778 57.0484 28.6245C57.0858 28.7864 57.1356 28.9234 57.1979 29.0386C57.3442 28.9078 57.4532 28.7957 57.531 28.7055C57.5839 28.6432 57.6431 28.637 57.7022 28.6837C57.7551 28.7366 57.7551 28.7957 57.7022 28.8549C57.5652 29.0074 57.4251 29.1382 57.2882 29.2441C57.4563 29.5056 57.5964 29.6301 57.7022 29.6239C57.7489 29.6021 57.7738 29.5056 57.7832 29.3375C57.7832 29.269 57.8143 29.2316 57.8766 29.2223C57.9451 29.2223 57.9824 29.2565 57.9918 29.325C57.9918 29.8013 57.8734 29.985 57.6337 29.876C57.4407 29.792 57.2664 29.627 57.1045 29.381C56.843 29.5585 56.6344 29.683 56.4725 29.7609C56.3884 29.792 56.3261 29.7795 56.2888 29.7266C56.2577 29.6488 56.2826 29.5959 56.3573 29.5647C56.5721 29.4713 56.7869 29.3468 57.0017 29.1849C56.9332 29.0479 56.871 28.8736 56.818 28.6681C56.6873 28.6992 56.5877 28.7179 56.5192 28.7241C56.4569 28.7397 56.4102 28.7459 56.3822 28.7459C56.2981 28.7615 56.2452 28.7335 56.2203 28.665C56.2203 28.5965 56.2639 28.5498 56.3479 28.528C56.3542 28.528 56.3853 28.5249 56.4413 28.5155C56.541 28.5 56.653 28.4782 56.7745 28.4471C56.7433 28.3007 56.7278 28.1824 56.7278 28.0921C56.6344 28.1139 56.5565 28.1295 56.4849 28.1388H56.4164C56.3386 28.1544 56.295 28.1264 56.2794 28.0579C56.2732 27.9894 56.3075 27.9427 56.3822 27.9209C56.4289 27.9302 56.5347 27.9178 56.7029 27.8867C56.6873 27.7715 56.6748 27.6438 56.6686 27.5068C56.6593 27.3699 56.6562 27.2765 56.6562 27.2298H56.6624ZM57.3068 27.1831C57.4002 27.2671 57.4874 27.3667 57.5715 27.4819C57.6088 27.5504 57.6088 27.6127 57.5715 27.6656C57.5185 27.703 57.4687 27.6936 57.422 27.6314C57.3909 27.6002 57.3535 27.5629 57.3068 27.5162C57.2539 27.4539 57.2072 27.3979 57.1698 27.3449C57.1169 27.292 57.1076 27.2422 57.1481 27.1955C57.1948 27.1488 57.2477 27.1457 57.3099 27.1831H57.3068Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M60.3194 27.1825C60.3412 27.1202 60.391 27.0953 60.4688 27.1015C60.5373 27.1233 60.5684 27.1669 60.5591 27.2292C60.5529 27.2665 60.5373 27.3163 60.5124 27.3786V27.391H62.0659C62.2278 27.3848 62.3025 27.4782 62.2963 27.6775V28.1943C62.287 28.708 62.2091 29.0224 62.0535 29.1376C61.9227 29.2435 61.7017 29.2652 61.3872 29.1936C61.3187 29.1781 61.2907 29.1314 61.3063 29.0567C61.3281 28.9882 61.3748 28.957 61.4433 28.9664C61.6363 29.0193 61.7795 29.0162 61.8791 28.9539C61.9943 28.8917 62.0503 28.6332 62.0503 28.1725V27.7242C62.0441 27.6619 62.0192 27.6277 61.9819 27.6214H60.3941C60.2851 27.7833 60.2073 27.8861 60.1512 27.9328H61.3125C61.5055 27.9328 61.5958 27.9981 61.5896 28.1289V28.5897C61.5958 28.7516 61.518 28.8232 61.3467 28.8076H60.3349V29.4178C60.3194 29.533 60.3723 29.5828 60.4968 29.5672H61.7857C62.0939 29.6046 62.2309 29.4801 62.1998 29.1874C62.1998 29.1189 62.2371 29.0816 62.315 29.0722C62.3928 29.0816 62.4302 29.122 62.4302 29.1999C62.4302 29.4614 62.3959 29.617 62.3274 29.67C62.2652 29.7478 62.1157 29.7852 61.8791 29.7852H60.4408C60.1948 29.8007 60.0828 29.6887 60.1077 29.452V28.0044C60.0703 28.0417 60.0205 28.0884 59.9582 28.1414C59.9427 28.1476 59.9302 28.1569 59.924 28.1631C59.8555 28.2161 59.7932 28.2192 59.7403 28.1756C59.6874 28.1133 59.6905 28.0542 59.7527 27.9919C60.0298 27.7615 60.2166 27.4938 60.3162 27.1887L60.3194 27.1825ZM60.4346 28.16C60.3661 28.16 60.3349 28.1943 60.3412 28.2628V28.5834H61.2845C61.3467 28.5928 61.3748 28.5554 61.3748 28.4807V28.2379C61.3748 28.1849 61.3436 28.1569 61.2845 28.1569H60.4346V28.16Z'
          }
          fill={'black'}
        />
        <Path
          d={
            'M23.5792 12.681C22.7355 11.775 21.5276 11.2987 20.2916 11.3205L15.7058 11.3952C15.6404 11.3983 15.5719 11.4014 15.5065 11.4014C15.4878 11.4014 15.4691 11.4014 15.4505 11.4014H15.3664C15.3259 11.4014 15.2886 11.3983 15.2574 11.3921C13.3459 11.2614 11.8359 9.67357 11.8359 7.73088C11.8359 6.14622 12.8415 4.79506 14.2487 4.28136V4.26891C14.3484 4.24089 14.4449 4.21598 14.5352 4.19108C14.5601 4.18485 14.585 4.17862 14.6099 4.16929C16.2724 3.72097 16.2101 3.67116 17.8913 3.74899C22.2561 3.97315 23.8999 9.7265 24.121 13.5683C24.0556 13.2725 23.847 12.9674 23.5792 12.6779V12.681Z'
          }
          fill={`url(#${id}_paint0_linear_7576_47160)`}
        />
        <Path
          d={
            'M17.0105 15.2871C17.0105 15.2871 16.9016 16.8126 15.7932 17.8525C14.6818 18.8923 13.3711 19.0075 13.3711 19.0075C13.3711 19.0075 14.881 19.2503 15.7279 20.1812C16.556 21.0934 16.8922 21.7472 17.0417 22.6158C17.1911 21.7472 17.5429 21.0716 18.3399 20.1314C19.14 19.1912 20.6562 19.0075 20.6562 19.0075C20.6562 19.0075 19.3424 18.8923 18.234 17.8525C17.1226 16.8126 17.0168 15.2871 17.0168 15.2871H17.0105Z'
          }
          fill={`url(#${id}_paint1_linear_7576_47160)`}
        />
        <Path
          fillRule={'evenodd'}
          clipRule={'evenodd'}
          d={
            'M9.69878 25.5078C10.1627 26.1834 10.941 26.5726 11.7598 26.5975C13.7025 26.6535 20.1874 26.7998 22.1114 26.4013C22.1675 26.3889 22.2235 26.3764 22.2765 26.364C23.5622 26.0433 24.4277 24.8385 24.3779 23.5153C24.3001 21.4232 24.0573 14.711 24.0822 13.7707C24.0822 13.7023 24.0728 13.6306 24.0573 13.559C23.8362 9.72659 22.1955 3.9888 17.8431 3.76465C26.3953 4.1507 31.8685 11.0279 32.0024 18.9637C32.1393 26.9991 25.3617 34.0849 17.0025 34.2188C16.9932 34.2188 16.987 34.2157 16.9776 34.2126C16.9558 34.2064 16.9372 34.2001 16.9154 34.197C11.2617 32.8147 9.79218 28.1666 9.375 24.8572C9.45906 25.1062 9.56802 25.3241 9.69567 25.5078H9.69878Z'
          }
          fill={`url(#${id}_paint2_linear_7576_47160)`}
        />
        <Path
          d={
            'M7.86191 31.5232C-0.291786 26.4454 1.60732 18.5097 1.9778 17.227C2.36696 15.6268 3.83332 14.4375 5.58298 14.4375C7.33265 14.4375 8.66825 15.5209 9.12901 17.0122C9.12901 17.0184 9.14147 17.1149 9.13524 17.1492C9.14769 17.8777 9.20685 21.8471 9.23798 23.8708C9.24421 24.2381 9.30336 24.5557 9.39364 24.8296C9.81394 28.164 11.299 32.8619 17.0461 34.2193C14.1009 34.266 10.9627 33.4566 7.85879 31.5232H7.86191Z'
          }
          fill={`url(#${id}_paint3_linear_7576_47160)`}
        />
        <Path
          d={
            'M7.65481 6.46099C6.06704 6.46099 4.78125 5.17521 4.78125 3.58743C4.78125 1.99965 6.07015 0.713867 7.65481 0.713867C9.23948 0.713867 10.5284 1.99965 10.5284 3.58743C10.5284 5.17521 9.24259 6.46099 7.65481 6.46099Z'
          }
          fill={`url(#${id}_paint4_linear_7576_47160)`}
        />
        <Path
          d={
            'M2.75788 11.4113C2.0823 11.4113 1.53125 10.8633 1.53125 10.1846C1.53125 9.50595 2.07919 8.95801 2.75788 8.95801C3.43658 8.95801 3.98452 9.50595 3.98452 10.1846C3.98452 10.8633 3.43658 11.4113 2.75788 11.4113Z'
          }
          fill={`url(#${id}_paint5_linear_7576_47160)`}
        />
        <Path
          d={
            'M7.65792 8.13574C8.79115 8.13574 9.70646 9.05416 9.70646 10.1843C9.70646 11.3144 8.78804 12.2328 7.65792 12.2328C6.52779 12.2328 5.60938 11.3144 5.60938 10.1843C5.60938 9.05416 6.52779 8.13574 7.65792 8.13574Z'
          }
          fill={`url(#${id}_paint6_linear_7576_47160)`}
        />
      </G>
      <Defs>
        <LinearGradient
          id={`${id}_paint0_linear_7576_47160`}
          x1={'11.8328'}
          y1={'8.64619'}
          x2={'24.121'}
          y2={'8.64619'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <LinearGradient
          id={`${id}_paint1_linear_7576_47160`}
          x1={'13.368'}
          y1={'18.9514'}
          x2={'20.6531'}
          y2={'18.9514'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <LinearGradient
          id={`${id}_paint2_linear_7576_47160`}
          x1={'29.5086'}
          y1={'9.91961'}
          x2={'10.0381'}
          y2={'29.3932'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <LinearGradient
          id={`${id}_paint3_linear_7576_47160`}
          x1={'9.28779'}
          y1={'34.2224'}
          x2={'9.28779'}
          y2={'14.4375'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <LinearGradient
          id={`${id}_paint4_linear_7576_47160`}
          x1={'4.78125'}
          y1={'3.58743'}
          x2={'10.5284'}
          y2={'3.58743'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <LinearGradient
          id={`${id}_paint5_linear_7576_47160`}
          x1={'1.53125'}
          y1={'10.1846'}
          x2={'3.98452'}
          y2={'10.1846'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <LinearGradient
          id={`${id}_paint6_linear_7576_47160`}
          x1={'5.60937'}
          y1={'10.1843'}
          x2={'9.70957'}
          y2={'10.1843'}
          gradientUnits={'userSpaceOnUse'}
        >
          <Stop stopColor={'#DABD94'} />
          <Stop offset={'1'} stopColor={'#AD8458'} />
        </LinearGradient>
        <ClipPath id={`${id}_clip0_7576_47160`}>
          <Rect width={'60.9524'} height={'36.5714'} fill={'black'} transform={'translate(1.52344 0.713867)'} />
        </ClipPath>
      </Defs>
    </Svg>
  )
}

function DynamicPromo({ qrSize }: { qrSize: number }) {
  const [promo, setPromo] = useState<{ kind: string; value: string } | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const hasLoggedError = useRef(false)
  const hasLoggedEmpty = useRef(false)

  useEffect(() => {
    let cancelled = false

    const loadPromo = async () => {
      try {
        const response = await fetch(`${BASE_URL}/mobile/public/scan2pay-qr/promo`, {
          headers: { accept: 'application/json' }
        })

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`)
        }

        const result = (await response.json()) as {
          data?: { kind?: string; value?: string }
        }

        const promoData =
          result?.data && result.data.kind ? { kind: result.data.kind, value: result.data.value ?? '' } : null
        if (!cancelled) setPromo(promoData)
      } catch (err) {
        if (!cancelled) {
          const requestError = err instanceof Error ? err : new Error('Unknown error loading Scan2Pay promo')
          setError(requestError)
          if (!hasLoggedError.current) {
            console.error('Failed to fetch Scan2Pay promo', requestError)
            hasLoggedError.current = true
          }
        }
      }
    }

    loadPromo()

    return () => {
      cancelled = true
    }
  }, [])

  if (error || !promo || (!promo.value && promo.kind === 'Text')) {
    if (error && !hasLoggedError.current) {
      console.warn('Scan2Pay promo suppressed due to error', error)
      hasLoggedError.current = true
    }
    if (!error && !promo && !hasLoggedEmpty.current) {
      console.info('Scan2Pay promo not configured; rendering without promo block')
      hasLoggedEmpty.current = true
    }
    if (!error && promo && !promo.value && promo.kind === 'Text' && !hasLoggedEmpty.current) {
      console.info('Scan2Pay promo has empty text value; skipping rendering')
      hasLoggedEmpty.current = true
    }
    return null
  }

  return (
    <View className={'w-full max-w-[360px] items-center rounded-lg border border-border-10 bg-background-primary p-4'}>
      {promo.kind === 'Image' ? (
        <Image
          source={{ uri: `data:image/png;base64,${promo.value}` }}
          style={{ width: qrSize, height: qrSize, resizeMode: 'contain' }}
        />
      ) : (
        <Text className={'text-left text-sm font-bold text-text-secondary'}>{promo.value}</Text>
      )}
    </View>
  )
}

export default function Scan2PayQrScreen() {
  const { t } = useTranslation()
  return (
    <>
      <Stack.Screen
        options={{
          header: () => <NavigationBar>{t('ui:scanToPay')}</NavigationBar>
        }}
      />
      <Scan2PayQrContent />
    </>
  )
}

function Scan2PayQrContent() {
  const { t } = useTranslation()
  const { link } = useLocalSearchParams<Record<string, string | string[]>>()
  const { countdown } = useAutomaticCountdown({ startTime: 300 })
  const [qrImageUri, setQrImageUri] = useState('')
  const [qrImageError, setQrImageError] = useState<Error | null>(null)
  const normalizedLink = Array.isArray(link) ? link?.[0] : link
  const url = (() => {
    if (!normalizedLink) return ''
    const trimmed = normalizedLink.trim()
    try {
      return decodeURIComponent(trimmed)
    } catch (err) {
      console.warn('Scan2Pay link parameter could not be decoded', { raw: normalizedLink, error: err })
      return ''
    }
  })()
  const { width } = useWindowDimensions()
  const qrSize = width > 540 ? 288 : 192

  useEffect(() => {
    console.info('Scan2Pay QR params', { rawLink: link, decodedUrl: url })
  }, [link, url])

  useEffect(() => {
    let cancelled = false

    if (Platform.OS !== 'web' || !url) {
      setQrImageUri('')
      setQrImageError(null)
      return
    }

    const generateQrImage = async () => {
      try {
        const QRCode = (await import('qrcode')) as QRCodeModule
        const imageUri = await QRCode.toDataURL(url, {
          errorCorrectionLevel: 'M',
          margin: 4,
          scale: 8,
          color: {
            dark: '#000000',
            light: '#ffffff'
          }
        })

        if (!cancelled) {
          setQrImageUri(imageUri)
          setQrImageError(null)
        }
      } catch (err) {
        if (!cancelled) {
          const error = err instanceof Error ? err : new Error('Unknown error generating Scan2Pay QR image')
          setQrImageUri('')
          setQrImageError(error)
          console.error('Failed to generate Scan2Pay QR image', error)
        }
      }
    }

    generateQrImage()

    return () => {
      cancelled = true
    }
  }, [url])

  if (!url) {
    return (
      <View className={'flex-1 items-center justify-center bg-background-secondary'}>
        <Text className={'text-base text-text-secondary'}>{t('ui:invalidPaymentLink')}</Text>
      </View>
    )
  }

  const handleOpenLink = () => {
    Linking.openURL(url)
  }

  return (
    <View className={'flex-1 items-center justify-start bg-background-secondary px-4 pt-6'}>
      <View className={'mb-4'}>
        <BigLogo />
      </View>
      <Text className={'mb-4 text-center text-sm text-text-secondary'}>
        {t('ui:scanToPayHint', { brand: marketPresentation.branding.displayName })}
      </Text>
      <View
        className={
          'relative w-full max-w-[360px] items-center gap-4 rounded-2xl border border-border-10 bg-background-primary px-4 py-6 shadow-1'
        }
      >
        {countdown > 0 ? (
          <>
            <Text className={'absolute right-4 top-4 text-xs text-primary'}>{formatRemainingTime(countdown)}</Text>
            <View className={'mt-2 rounded-2xl border border-border-10 bg-white p-4 shadow-1'}>
              {Platform.OS === 'web' && qrImageUri && !qrImageError ? (
                <Image source={{ uri: qrImageUri }} style={{ width: qrSize, height: qrSize, resizeMode: 'contain' }} />
              ) : (
                <NativeQRCode value={url} size={qrSize} />
              )}
            </View>
          </>
        ) : (
          <Text className={'text-base text-text-secondary'}>{t('ui:paymentQrExpired')}</Text>
        )}
      </View>
      <View className={'h-3'} />
      <DynamicPromo qrSize={qrSize} />
      <View className={'h-4'} />
      <Button size={'md'} onPress={handleOpenLink}>
        {t('ui:signInToPayInBrowser')}
      </Button>
      <View className={'h-4'} />
      <Pressable onPress={() => Linking.openURL(marketPresentation.branding.websiteUrl)}>
        <Text className={'text-center text-sm text-primary'}>
          {t('ui:visitOfficialWebsite', { brand: marketPresentation.branding.displayName })}
        </Text>
      </Pressable>
    </View>
  )
}
